import { currency } from '@snn/abacus-core'
import type { Currency, User } from '@/types/database.types'
import { CURRENCY_THEMES, EXTENDED_CURRENCY_THEMES } from '@/constants/themes'
import { rateService } from '@/services/rateService'
import type { DashboardRates } from './types'

/**
 * Para birimi dönüştürür
 */
export function convertCurrency(
  amount: number,
  rate: number,
  from: Currency,
  to: Currency
): number {
  if (from === to) return amount
  // SSOT: cevrim hesabi core'da (currency.convert kurus bazli)
  const amountMinor = Math.round(amount * 100)
  const kurus = currency.convert(amountMinor, rate)
  return kurus !== null ? kurus / 100 : 0
}

/**
 * İşlem tutarını TRY'ye çevirir (Asenkron - Tarih destekli)
 */
export async function convertToTRY(
  amount: number,
  currency: Currency | string,
  date?: string | Date
): Promise<number> {
  if (currency === 'TRY') return amount

  const targetDate = date
    ? typeof date === 'string'
      ? date
      : date.toISOString().split('T')[0]
    : undefined
  let rates = targetDate
    ? await rateService.getRateByDate(targetDate)
    : await rateService.getTodayRates()

  if (!rates && targetDate) {
    rates = await rateService.getTodayRates()
  }

  if (!rates) return amount

  switch (currency) {
    case 'USD':
      return convertCurrency(amount, rates.usd_try, 'USD', 'TRY')
    case 'EUR':
      return convertCurrency(amount, rates.eur_try, 'EUR', 'TRY')
    case 'GRAM_GOLD':
    case 'GRAM_24_GOLD':
      return convertCurrency(amount, rates.gram_24_try, 'GRAM_GOLD', 'TRY')
    case 'GRAM_22_GOLD':
      return convertCurrency(amount, rates.gram_22_try, 'GRAM_GOLD', 'TRY')
    case 'QUARTER_GOLD':
      return convertCurrency(
        amount,
        rates.quarter_gold_try || 0,
        'GRAM_GOLD',
        'TRY'
      )
    case 'HALF_GOLD':
      return convertCurrency(
        amount,
        rates.half_gold_try || 0,
        'GRAM_GOLD',
        'TRY'
      )
    case 'FULL_GOLD':
      return convertCurrency(
        amount,
        rates.full_gold_try || 0,
        'GRAM_GOLD',
        'TRY'
      )
    case 'GRAM_SILVER':
      return convertCurrency(amount, rates.gram_silver_try || 0, 'GRAM_SILVER', 'TRY')
    default:
      return amount
  }
}

/**
 * Tutarı formatlı string'e çevirir
 */
export function formatCurrency(
  amount: number,
  currency: Currency,
  locale: string = 'tr-TR',
  showSymbol: boolean = true,
  minimumFractionDigits?: number
): string {
  const theme =
    (EXTENDED_CURRENCY_THEMES as any)[currency] ||
    CURRENCY_THEMES[currency as keyof typeof CURRENCY_THEMES]
  if (!theme) return amount.toString()

  const formatted = new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits:
      minimumFractionDigits !== undefined ? minimumFractionDigits : amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount)

  const suffix = showSymbol ? theme.symbol : ''
  return `${formatted}${suffix}`
}

/**
 * Sayıyı güvenli bir şekilde yuvarlar
 */
export function roundSafe(value: number, decimals: number = 2): number {
  const multiplier = Math.pow(10, decimals)
  return Math.round(value * multiplier) / multiplier
}

/**
 * İki sayının eşit olup olmadığını kontrol eder
 */
export function areNumbersEqual(a: number, b: number, tolerance: number = 0.001): boolean {
  return Math.abs(a - b) < tolerance
}

/**
 * İki değer arasındaki yüzdelik değişimi hesaplar
 */
export function calculatePercentageChange(current: number, previous: number): number {
  if (!previous || previous === 0) return 0
  const change = ((current - previous) / previous) * 100
  return Math.round(change * 100) / 100
}

/**
 * Toplam pot miktarını hesaplar
 * @param memberCount - Üye sayısı
 * @param amountPerMember - Üye başı ödeme miktarı
 * @returns Toplam pot
 */
export function calculatePotAmount(memberCount: number, amountPerMember: number): number {
  // O ay potu alan kişi ödeme yapmaz
  const payingMembers = memberCount - 1
  return Math.round(payingMembers * amountPerMember * 100) / 100
}

/**
 * Dinamik ödeme açıklaması oluşturur
 */
export function generatePaymentDescription(groupName: string, date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const monthName = new Intl.DateTimeFormat('tr-TR', { month: 'long' }).format(d)
  const year = d.getFullYear()
  return `${groupName} ${monthName} ${year} Ödemesi`
}

/**
 * Para birimi adını halk diline çevirir
 */
export function formatCurrencyName(currency: Currency | string): string {
  switch (currency) {
    case 'USD':
      return 'Dolar'
    case 'EUR':
      return 'Euro'
    case 'TRY':
      return 'TL'
    case 'GRAM_GOLD':
    case 'GRAM_24_GOLD':
      return 'Gram Altın (24 ayar)'
    case 'GRAM_22_GOLD':
      return 'Gram Altın (22 ayar)'
    case 'QUARTER_GOLD':
      return 'Çeyrek Altın'
    case 'HALF_GOLD':
      return 'Yarım Altın'
    case 'FULL_GOLD':
      return 'Tam Altın'
    case 'GRAM_SILVER':
      return 'Gram Gümüş'
    default:
      return currency
  }
}

/**
 * Tutar ve para birimini konuşma dilinde formatlar (Örn: "100 Gram Altın", "1 Adet Çeyrek Altın")
 */
export function formatAmountWithCurrency(amount: string | number, currency: string | Currency): string {
  const numAmount = Number(amount).toLocaleString('tr-TR')
  switch (currency) {
    case 'GRAM_GOLD': return `${numAmount} Gram Altın`
    case 'GRAM_SILVER': return `${numAmount} Gram Gümüş`
    case 'GRAM_22_GOLD': return `${numAmount} Gram 22 Ayar Altın`
    case 'GRAM_24_GOLD': return `${numAmount} Gram 24 Ayar Altın`
    case 'QUARTER_GOLD': return `${numAmount} Adet Çeyrek Altın`
    case 'HALF_GOLD': return `${numAmount} Adet Yarım Altın`
    case 'FULL_GOLD': return `${numAmount} Adet Tam Altın`
    case 'USD': return `${numAmount} USD`
    case 'EUR': return `${numAmount} EUR`
    case 'TRY': return `${numAmount} TL`
    default: return `${numAmount} ${currency}`
  }
}

/**
 * Payment ActionDrawer için gerekli tüm verileri hazırlar
 */
export async function getPaymentDrawerData(params: {
  amount: number
  currency: Currency
  date: string | Date
  groupName: string
  isReceiver: boolean
  currentUser: User | null
  receiverUser: User | null
}) {
  const { amount, currency, date, groupName, isReceiver, currentUser: _currentUser, receiverUser } = params

  const targetDate = typeof date === 'string' ? date : date.toISOString().split('T')[0]
  const historicalRates = await rateService.getRateByDate(targetDate)
  const todayRates = await rateService.getTodayRates()

  // Kur fallback zinciri: historical → today → null (asla 1 hardcode)
  const effectiveRates = historicalRates || todayRates
  const rateDate = historicalRates ? targetDate : (todayRates ? new Date().toISOString().split('T')[0] : targetDate)

  let tryAmount = amount
  let currentTryAmount = amount
  let historicalRate = 0
  let currentRate = 0
  let rateAvailable = false

  if (currency !== 'TRY' && effectiveRates) {
    rateAvailable = true
    const currencyStr = currency
    switch (currencyStr) {
      case 'USD':
        historicalRate = effectiveRates.usd_try
        tryAmount = convertCurrency(amount, historicalRate, 'USD', 'TRY')
        if (todayRates) {
          currentRate = todayRates.usd_try
          currentTryAmount = convertCurrency(amount, currentRate, 'USD', 'TRY')
        }
        break
      case 'EUR':
        historicalRate = effectiveRates.eur_try
        tryAmount = convertCurrency(amount, historicalRate, 'EUR', 'TRY')
        if (todayRates) {
          currentRate = todayRates.eur_try
          currentTryAmount = convertCurrency(amount, currentRate, 'EUR', 'TRY')
        }
        break
      case 'GRAM_GOLD':
      case 'GRAM_24_GOLD':
        historicalRate = effectiveRates.gram_24_try
        tryAmount = convertCurrency(amount, historicalRate, 'GRAM_GOLD', 'TRY')
        if (todayRates) {
          currentRate = todayRates.gram_24_try
          currentTryAmount = convertCurrency(amount, currentRate, 'GRAM_GOLD', 'TRY')
        }
        break
      case 'GRAM_22_GOLD':
        historicalRate = effectiveRates.gram_22_try
        tryAmount = convertCurrency(amount, historicalRate, 'GRAM_GOLD', 'TRY')
        if (todayRates) {
          currentRate = todayRates.gram_22_try
          currentTryAmount = convertCurrency(amount, currentRate, 'GRAM_GOLD', 'TRY')
        }
        break
      case 'QUARTER_GOLD':
        historicalRate = effectiveRates.quarter_gold_try || 0
        tryAmount = convertCurrency(amount, historicalRate, 'GRAM_GOLD', 'TRY')
        if (todayRates) {
          currentRate = todayRates.quarter_gold_try || 0
          currentTryAmount = convertCurrency(amount, currentRate, 'GRAM_GOLD', 'TRY')
        }
        break
      case 'HALF_GOLD':
        historicalRate = effectiveRates.half_gold_try || 0
        tryAmount = convertCurrency(amount, historicalRate, 'GRAM_GOLD', 'TRY')
        if (todayRates) {
          currentRate = todayRates.half_gold_try || 0
          currentTryAmount = convertCurrency(amount, currentRate, 'GRAM_GOLD', 'TRY')
        }
        break
      case 'FULL_GOLD':
        historicalRate = effectiveRates.full_gold_try || 0
        tryAmount = convertCurrency(amount, historicalRate, 'GRAM_GOLD', 'TRY')
        if (todayRates) {
          currentRate = todayRates.full_gold_try || 0
          currentTryAmount = convertCurrency(amount, currentRate, 'GRAM_GOLD', 'TRY')
        }
        break
      case 'GRAM_SILVER':
        historicalRate = effectiveRates.gram_silver_try || 0
        tryAmount = convertCurrency(amount, historicalRate, 'GRAM_SILVER', 'TRY')
        if (todayRates) {
          currentRate = todayRates.gram_silver_try || 0
          currentTryAmount = convertCurrency(amount, currentRate, 'GRAM_SILVER', 'TRY')
        }
        break
    }
  } else if (currency === 'TRY') {
    rateAvailable = true
    historicalRate = 1
    currentRate = 1
  }

  const description = generatePaymentDescription(groupName, date)
  // Borçlu alacaklının IBAN'ını her zaman görmeli (ödeme yapabilmek için)
  const receiverIBAN = (receiverUser?.ibans && receiverUser.ibans.length > 0) ? receiverUser.ibans[0].iban : null
  const receiverFullName = receiverUser?.full_name || 'Bilinmiyor'
  const percentageChange = calculatePercentageChange(currentTryAmount, tryAmount)
  const tryDifference = currentTryAmount - tryAmount

  return {
    tryAmount,
    currentTryAmount,
    historicalRate,
    currentRate,
    rateDate,
    rateAvailable,
    formattedHistoricalRate: historicalRate.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    formattedCurrentRate: currentRate.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    formattedTryAmount: formatCurrency(tryAmount, 'TRY'),
    formattedCurrentTryAmount: formatCurrency(currentTryAmount, 'TRY'),
    tryDifference,
    formattedTryDifference: formatCurrency(Math.abs(tryDifference), 'TRY'),
    percentageChange,
    description,
    receiverIBAN,
    receiverIbans: receiverUser?.ibans || [],
    receiverFullName,
    showSensitiveInfo: !isReceiver,
    isTRY: currency === 'TRY',
  }
}

export function calculateDashboardDisplayData(
  today: any | null,
  yesterday: any | null
): DashboardRates {
  const defaultItem = { value: '...', change: '...', trendUp: true, rawChange: 0 }
  if (!today) {
    return {
      gram_24_try: defaultItem,
      gram_22_try: defaultItem,
      quarter_gold_try: defaultItem,
      half_gold_try: defaultItem,
      full_gold_try: defaultItem,
      usd_try: defaultItem,
      eur_try: defaultItem,
      gram_silver_try: defaultItem,
    }
  }

  const createItem = (currentVal: number, prevVal: number | undefined) => {
    const change = prevVal ? calculatePercentageChange(currentVal, prevVal) : 0
    const trendUp = change >= 0
    return {
      value: formatCurrency(currentVal, 'TRY'),
      change: `%${Math.abs(change).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      trendUp: trendUp,
      rawChange: change,
    }
  }

  return {
    gram_24_try: createItem(today.gram_24_try, yesterday?.gram_24_try),
    gram_22_try: createItem(today.gram_22_try, yesterday?.gram_22_try),
    quarter_gold_try: createItem(
      today.quarter_gold_try || 0,
      yesterday?.quarter_gold_try || undefined
    ),
    half_gold_try: createItem(
      today.half_gold_try || 0,
      yesterday?.half_gold_try || undefined
    ),
    full_gold_try: createItem(
      today.full_gold_try || 0,
      yesterday?.full_gold_try || undefined
    ),
    usd_try: createItem(today.usd_try, yesterday?.usd_try),
    eur_try: createItem(today.eur_try, yesterday?.eur_try),
    gram_silver_try: createItem(
      today.gram_silver_try || 0,
      yesterday?.gram_silver_try || undefined
    ),
  }
}

export const AVAILABLE_WIDGETS = [
  { id: 'gram_24_try', label: 'Gram Altın (24 Ayar)' },
  { id: 'gram_22_try', label: 'Gram Altın (22 Ayar)' },
  { id: 'quarter_gold_try', label: 'Çeyrek Altın' },
  { id: 'half_gold_try', label: 'Yarım Altın' },
  { id: 'full_gold_try', label: 'Tam Altın' },
  { id: 'gram_silver_try', label: 'Gram Gümüş' },
  { id: 'usd_try', label: 'Dolar Kuru' },
  { id: 'eur_try', label: 'Euro Kuru' },
] as const
